import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Checkbox } from 'primereact/checkbox';

import FirebaseDeviceListener from '../../../../Services/FirebaseDeviceListener';
import DeviceLastLoginListener from '../../../../Services/DeviceLastLoginListener';
import { resolveDeviceOnline, resolveDeviceStatus } from '../../../../../utils';
import { formatDateTime } from '../../../../../utils/datetime';

function DeviceCard({ item, ids, setIds }) {
  const { t } = useTranslation();
  const [campaignTag, setCampaignTag] = useState('');
  const [onlineTimes, setOnlineTimes] = useState({});
  const [currentCampaignName, setCurrentCampaignName] = useState('');
  const { rtdbConnected } = useSelector((state) => state.device);

  const isOnline = useMemo(
    () => resolveDeviceOnline(item?.connectivity, item?.online),
    [item?.connectivity, item?.online]
  );
  // "Playing now" must mean actively playing, not merely reachable (Phase 3:
  // a standby/app_closed device is reachable but has nothing running) —
  // gates playingBlock below on this, not the isOnline boolean.
  const deviceStatus = useMemo(
    () => resolveDeviceStatus(item?.connectivity, item?.presence_status, item?.online),
    [item?.connectivity, item?.presence_status, item?.online]
  );

  const [realtimeData, setRealtimeData] = useState({
    id: '',
    name: '',
    schedule_id: '',
    tag: '',
    is_playing: '',
    screen_angle: '',
    is_muted: '',
    download_percentage: { campaign_id: '', percentage: 0 },
    media_playing: { currentMediaId: '', start_time: '' },
    corner: { id: '', start_time: '' },
    playground: { id: '', start_time: '' },
    banner: { id: '', start_time: '' },
  });

  useEffect(() => {
    if (campaignTag !== realtimeData.tag) setCampaignTag(realtimeData?.tag);
  }, [realtimeData?.tag]);

  const handleOnlineTimeUpdate = useCallback((deviceId, secondsOnline) => {
    setOnlineTimes((prev) => ({ ...prev, [deviceId]: secondsOnline }));
  }, []);

  useEffect(() => {
    // Trust RTDB's realtimeData.name only while RTDB is actually connected —
    // once it's not, that value can be a populated-but-stale campaign name
    // (device switched via the REST fallback and can't write RTDB). NOTE:
    // this endpoint (/company/devices, via CompanyController::getDevicesGroups)
    // doesn't serve current_campaign_name yet, unlike the main Devices-page
    // grid — this falls back to realtimeData.name unchanged until it does.
    const isRtdbLive = rtdbConnected?.[item?.serial_name] === true;
    setCurrentCampaignName(
      (isRtdbLive || !item?.current_campaign_name) ? realtimeData.name : item.current_campaign_name
    );
  }, [realtimeData?.media_playing, realtimeData?.id, rtdbConnected, item?.serial_name, item?.current_campaign_name]);

  useEffect(() => {
    let interval;
    if (isOnline && realtimeData?.first_login) {
      interval = setInterval(() => {
        const secondsOnline = moment().diff(moment(realtimeData.first_login), 'seconds');
        handleOnlineTimeUpdate(item.id, secondsOnline);
      }, 10000);
    } else {
      handleOnlineTimeUpdate(item.id, 0);
    }
    return () => clearInterval(interval);
  }, [isOnline, realtimeData?.first_login]);

  const onDeviceChange = (e) => {
    const next = new Set(ids.map(String));
    if (e.checked) next.add(String(e.value));
    else next.delete(String(e.value));
    setIds(Array.from(next));
  };

  // --- UI ---
  const playingBlock = deviceStatus === 'online' ? (
    <>
      <div className="text-uppercase text-muted small">{t('show.show_playing_now')}</div>
      <div className="d-flex align-items-center">
        {(() => {
          let icon = 'fas fa-bolt me-2';
          let label = currentCampaignName || '---';

          if (realtimeData.name === 'Downloading...') {
            icon = 'fas fa-bolt me-2';
            label = realtimeData.name || '---';
          } else if (realtimeData.name === 'Updating...') {
            icon = 'fas fa-bolt me-2';
            label = realtimeData.name || 'Updating...';
          } else if (realtimeData.tag === 'scheduled') {
            icon = 'fas fa-calendar-days me-2';
            label = currentCampaignName || '---';
          } else if (realtimeData.tag === 'prayer' || realtimeData.name === 'Prayer break') {
            icon = 'fas fa-mosque me-2';
            label = realtimeData.name || '---';
          }

          return (
            <>
              <i className={icon} />
              <span className="fw-medium">{label}</span>
            </>
          );
        })()}
      </div>
    </>
  ) : (
    <>
      <div className="text-uppercase text-muted small">{t('show.show_last_login')}</div>
      <div className="d-flex align-items-center">
        <i className="fa-solid fa-clock me-2" />
        <span className="text-muted">{formatDateTime(item?.last_login || item?.created_at)}</span>
      </div>
    </>
  );

  return (
    <>
      {/* show on md and biffer hide on small */}
      <div className="row align-items-center gy-2 gx-2 d-none d-md-flex">
        {/* Icon */}
        <div className="col-auto">
          <div
            className="bg-secondary rounded-2 d-flex align-items-center justify-content-center"
            style={{ width: 52, height: 44 }}
          >
            <i className="bx bx-desktop text-white fs-4" />
          </div>
        </div>

        {/* Device name — 3 cols on md+, wide on phones */}
        <div className="col-6 col-md-3">
          <h6 className="mb-0 text-truncate fw-semibold">{item?.name ?? '--'}</h6>
        </div>

        {/* Status — stays tight at right on md+, inline on phone */}
        <div className="col-auto ms-auto order-md-4 d-none d-md-block">
          {deviceStatus === 'content_corrupted' ? (
            <span className="badge bg-label-warning px-3 py-2">{t('status.content_corrupted')}</span>
          ) : isOnline ? (
            <span className="badge bg-label-success px-3 py-2">{t('status.online')}</span>
          ) : deviceStatus === 'scheduled_off' ? (
            <span className="badge bg-label-secondary px-3 py-2">{t('status.scheduled_off')}</span>
          ) : (
            <span className="badge bg-label-danger px-3 py-2">{t('status.offline')}</span>
          )}
        </div>

        {/* Checkbox — always far right */}
        <div className="col-auto order-sm-4">
          <Checkbox
            inputId={item.id}
            name="campaign"
            value={item.id}
            onChange={onDeviceChange}
            checked={ids.indexOf(String(item.id)) !== -1}
          />
        </div>

        {/* Playing / Last login — full width on phone, 4 cols on md+ */}
        <div className="col-12 col-md-4 order-md-3 d-none d-md-block">
          <div className="w-100">
            {isOnline ? (
              <>
                <div className='fs-10 text-light text-uppercase'>{t('show.show_playing_now')}</div>
                <div className="*:flex justify-center text-center items-center">
                  {(() => {
                    let icon = "fas fa-bolt fs-8 me-1";
                    let label = currentCampaignName || "---";

                    if (realtimeData.name === "Downloading...") {
                      icon = "fas fa-bolt fs-8 me-1";
                      label = realtimeData.name || "---";
                    } else if (realtimeData.name === "Updating...") {
                      icon = "fas fa-bolt fs-8 me-1";
                      label = realtimeData.name || "Updating...";
                    } else if (
                      realtimeData.tag === "scheduled"
                    ) {
                      icon = "fas fa-calendar-days fs-8 me-1";
                      label = currentCampaignName || "---";
                    } else if (
                      realtimeData.tag === "prayer" ||
                      realtimeData.name === "Prayer break"
                    ) {
                      icon = "fas fa-mosque fs-8 me-1";
                      label = realtimeData.name || "---";
                    }

                    return (
                      <div className='flex items-center'>
                        <i className={icon}></i>
                        <span className='  fs-15'>{label}</span>
                      </div>
                    );
                  })()}

                </div>

              </>
            ) : (
              <>
                <div className=" fs-10 text-uppercase text-muted ">{t('show.show_last_login')}</div>
                <div className="d-flex align-items-center">
                  <i className="fa-solid fa-clock me-2" />
                  <span className=" fs-8 text-muted">
                    {formatDateTime(item?.last_login || item?.created_at)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* show on small  and  hide other wise */}

      <div className=" d-flex d-md-none gap-2   align-items-center gy-2 gx-2">
        {/* Icon */}
          <div
            className="bg-secondary rounded-2 d-flex align-items-center justify-content-center"
            style={{ width: 52, height: 44 }}
          >
            <i className="bx bx-desktop text-white fs-4" />
          </div>

        <div className="md:hidden flex-1 flex justify-between items-center ">
          <h6 className="mb-0 text-truncate fw-semibold">{item?.name ?? '--'}</h6>
          <div className="col-auto order-sm-4">
            <Checkbox
              inputId={item.id}
              name="campaign"
              value={item.id}
              onChange={onDeviceChange}
              checked={ids.indexOf(String(item.id)) !== -1}
            />
          </div>
        </div>
      </div>



      {/* Listeners */}
      <FirebaseDeviceListener
        setRealtimeData={setRealtimeData}
        path={`Devices/${item.serial_name}/DeviceState`}
      />
      <DeviceLastLoginListener
        serialName={item.serial_name}
        path={`Devices/${item.serial_name}/presence`}
        apiOnline={item.online}
        apiPresenceStatus={item.presence_status}
      />



    </>
  );
}

export default DeviceCard;
